import { htmlTypographyPreview } from './htmlTypography';
import { findHtmlImageIndex, HTML_IMAGE_SELECTOR } from './htmlImages';
import { HTML_CTA_SELECTOR } from './htmlCtas';
import { findHtmlTextPath, htmlNodeAtPath } from './htmlTexts';
import { useHtmlCtaSelection, useHtmlTextSelection, useHtmlImageSelection } from './PageBuilderContext';
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css';
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { Grid } from "@splidejs/splide-extension-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { ReactComponent as ImageIcon } from '../../assets/images/icons/ic-image.svg';
import { ReactComponent as VideoIcon } from '../../assets/images/icons/ic-lessons-active.svg';
import { BlockType } from '../../common/constants';
import { Each } from '../../common/Each';
import useClickOutside from '../../common/hooks/useClickOutside';
import Button from '../Button';
import ContactFormBlock from '../ContactFormBlock';
import FAQCollapsible from '../FAQCollapsible';
import SvgRenderer from "../SvgRenderer";
import CollapsableBlock from './CollapsableBlock';
import EditionsCarousel from "./EditionsCarousel";
import useEditMode from "./hooks/useEditMode";
import styles from './PageBlock.module.css';

// Overlay colors
const MARGIN_OVERLAY_COLOR = 'rgba(255, 140, 0, 0.18)'; // orange
const PADDING_OVERLAY_COLOR = 'rgba(0, 200, 80, 0.18)'; // green

const PageBlock = ({
    element,
    block,
    view,
    grid,
    fontScale = 1,
    viewportScale = 1,
    previewMode = false,
    selectedBlock,
    selectedBlockIds = [],
    parentSelected,
    insideCarousel = false,
    viewportSize = null,
    onBlockAdd = () => { },
    onBlockSelect = () => { },
    onDataUpdate = () => { },
    onContentOrder = () => { },
    openBlockDialog = () => { },
    onInnerBlockSelect = () => { },
    onRequestToolbarTab = () => { }
}) => {

    const { editMode, setEditMode, onClick } = useEditMode(block.id);

    const canAddBlock = useMemo(() => {
        return [BlockType.Section, BlockType.Column, BlockType.Row, BlockType.Collapsable, BlockType.Carousel].includes(block.type);
    }, [block.type]);

    const blockRef = useRef(null);
    const selectedHtmlCta = useHtmlCtaSelection();
    const selectedHtmlText = useHtmlTextSelection();
    const selectedHtmlImage = useHtmlImageSelection();
    const handleHtmlInteraction = event => {
        if (previewMode) return;
        const target = event.target.closest?.(HTML_CTA_SELECTOR);
        const htmlImageIndex = findHtmlImageIndex(event.currentTarget, event.target);
        if (event.type === 'pointerdown' && !target && htmlImageIndex < 0) return;
        event.preventDefault();
        event.stopPropagation();
        const htmlTextPath = htmlImageIndex < 0 ? findHtmlTextPath(event.currentTarget, event.target) : null;
        if (target && event.currentTarget.contains(target)) {
            const index = Array.from(event.currentTarget.querySelectorAll(HTML_CTA_SELECTOR)).indexOf(target);
            onBlockSelect(block.id, { htmlCtaIndex: index, htmlTextPath, htmlImageIndex });
        } else if (htmlImageIndex >= 0) {
            onBlockSelect(block.id, { htmlImageIndex });
        } else {
            onBlockSelect(block.id, htmlTextPath ? { htmlTextPath } : undefined);
        }
        onRequestToolbarTab('style');
        onClick(event);
    };
    useEffect(() => {
        if (previewMode || block.type !== BlockType.HTML || selectedBlock?.id !== block.id || selectedHtmlCta?.blockId !== block.id) return;
        const target = blockRef.current?.querySelectorAll(HTML_CTA_SELECTOR)[selectedHtmlCta.index];
        target?.setAttribute('data-builder-cta-selected', 'true');
        return () => target?.removeAttribute('data-builder-cta-selected');
    }, [previewMode, block.type, block.id, block.data?.html, selectedBlock?.id, selectedHtmlCta]);

    useEffect(() => {
        if (previewMode || block.type !== BlockType.HTML || selectedBlock?.id !== block.id || selectedHtmlText?.blockId !== block.id) return;
        const target = htmlNodeAtPath(blockRef.current, selectedHtmlText.path);
        target?.setAttribute?.('data-builder-text-selected', 'true');
        return () => target?.removeAttribute?.('data-builder-text-selected');
    }, [previewMode, block.type, block.id, block.data?.html, selectedBlock?.id, selectedHtmlText]);

    useEffect(() => {
        if (previewMode || block.type !== BlockType.HTML || selectedBlock?.id !== block.id || selectedHtmlImage?.blockId !== block.id) return;
        const target = blockRef.current?.querySelectorAll(HTML_IMAGE_SELECTOR)[selectedHtmlImage.index];
        target?.setAttribute('data-builder-image-selected', 'true');
        return () => target?.removeAttribute('data-builder-image-selected');
    }, [previewMode, block.type, block.id, block.data?.html, selectedBlock?.id, selectedHtmlImage]);

    const carouselContainerRef = useRef(null);
    const rootFontSizeRef = useRef(null);
    const [content, setContent] = useState(block.content ?? []);
    const blockStyles = {
        section: styles.section,
        column: styles.column,
        row: styles.row,
        text: styles.text,
        markdown: styles.markdown,
        image: styles.image,
        video: styles.video,
        button: styles.button,
        collapsable: styles.collapsable,
        carousel: styles.carousel,
        html: styles.html,
        form: styles.form,
        overlay: styles.overlay,
    };
    const blockSelectedStyles = {
        row: styles.rowSelected,
        column: styles.columnSelected,
    }
    const viewportWidth = viewportSize?.width ?? null;
    const viewportHeight = viewportSize?.height ?? null;

    useClickOutside(blockRef, (e) => {
        if (typeof (e.target.className) === 'string' && !e.target.className.includes('Dialog_') && !e.target.className.includes('PageBuilder_blocks')) {
            setEditMode(false)
        }
        if (!isInPageBuilder(e.target)) {
            onBlockSelect(null)
        }
    });


    useEffect(() => {
        setContent(block.content ?? []);
    }, [block.content])

    useEffect(() => {
        if (selectedBlock?.id === block.id) {
            setEditMode(true);
        }
        else {
            setEditMode(false)
        }
    }, [selectedBlock?.id, block.id, setEditMode])

    const isParentOfSelected = useMemo(() => {
        if (!Array.isArray(selectedBlockIds) || selectedBlockIds.length === 0) {
            return false;
        }
        if (!Array.isArray(block.content) || block.content.length === 0) {
            return false;
        }
        return block.content.some((child) => selectedBlockIds.includes(child.id));
    }, [block.content, selectedBlockIds]);

    const isInPageBuilder = (target) => {
        if (typeof target.className === 'string' && target.className.includes('PageBuilder')) {
            return true;
        }
        if (target.parentNode) {
            return isInPageBuilder(target.parentNode);
        }
        return false
    }

    const getRootFontSize = () => {
        if (rootFontSizeRef.current !== null) {
            return rootFontSizeRef.current;
        }
        if (typeof window === 'undefined' || !window.document?.documentElement) {
            rootFontSizeRef.current = 16;
            return rootFontSizeRef.current;
        }
        const computedStyle = typeof window.getComputedStyle === 'function'
            ? window.getComputedStyle(window.document.documentElement)
            : null;
        const size = computedStyle ? parseFloat(computedStyle.fontSize) : NaN;
        rootFontSizeRef.current = Number.isFinite(size) ? size : 16;
        return rootFontSizeRef.current;
    };

    const convertLengthToPx = (value, axisLength) => {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value !== 'string') {
            return null;
        }
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === 'auto') {
            return null;
        }
        const lowerCased = trimmed.toLowerCase();
        const numeric = parseFloat(lowerCased);
        if (Number.isNaN(numeric)) {
            return null;
        }
        if (lowerCased.endsWith('vh')) {
            if (!viewportHeight) {
                return null;
            }
            return (viewportHeight * numeric) / 100;
        }
        if (lowerCased.endsWith('vw')) {
            if (!viewportWidth) {
                return null;
            }
            return (viewportWidth * numeric) / 100;
        }
        if (lowerCased.endsWith('%')) {
            if (!axisLength) {
                return null;
            }
            return (axisLength * numeric) / 100;
        }
        if (lowerCased.endsWith('rem') || lowerCased.endsWith('em')) {
            return numeric * getRootFontSize();
        }
        if (lowerCased.endsWith('px')) {
            return numeric;
        }
        return numeric;
    };

    const clampLengthToAxis = (value, axisLength, fallback) => {
        if (!axisLength) {
            return value ?? fallback ?? null;
        }
        const px = convertLengthToPx(value ?? fallback, axisLength);
        if (px === null) {
            return value ?? fallback ?? null;
        }
        const clamped = Math.min(Math.max(px, 0), axisLength);
        return `${clamped}px`;
    };

    const normalizeFixedBlockStyle = (style) => {
        if (!style || style.position !== 'fixed') {
            return style;
        }
        const normalized = { ...style };

        if (viewportHeight) {
            normalized.height = clampLengthToAxis(normalized.height, viewportHeight, '100%') ?? normalized.height;
            normalized.maxHeight = `${viewportHeight}px`;
            if (normalized.top !== undefined) {
                normalized.top = clampLengthToAxis(normalized.top, viewportHeight);
            }
            if (normalized.bottom !== undefined) {
                normalized.bottom = clampLengthToAxis(normalized.bottom, viewportHeight);
            }
        }

        if (viewportWidth) {
            if (normalized.width !== undefined) {
                normalized.width = clampLengthToAxis(normalized.width, viewportWidth);
            }
            normalized.maxWidth = normalized.maxWidth ?? `${viewportWidth}px`;
            if (normalized.left !== undefined) {
                normalized.left = clampLengthToAxis(normalized.left, viewportWidth);
            }
            if (normalized.right !== undefined) {
                normalized.right = clampLengthToAxis(normalized.right, viewportWidth);
            }
        }

        if (normalized.overflow === undefined) {
            normalized.overflow = 'auto';
        }
        normalized.boxSizing = normalized.boxSizing ?? 'border-box';
        return normalized;
    };

    // Aggiunge il blocco di typo type al contenuto del blocco corrente
    const handleBlockAdd = (id, type) => {
        setEditMode(false);
        onBlockAdd(id, type);
    };

    const findBlockId = useCallback((element) => {
        let current = element;
        while (current) {
            if (current.id) {
                return current.id;
            }
            current = current.parentNode;
        }
        return null;
    }, []);

    const findDirectChildBlockId = useCallback((element) => {
        const directChildIds = new Set(
            Array.isArray(block.content)
                ? block.content.map((child) => child?.id).filter(Boolean)
                : []
        );

        if (directChildIds.size === 0) {
            return null;
        }

        let current = element;
        while (current) {
            if (current.id === block.id) {
                break;
            }
            if (current.id && directChildIds.has(current.id)) {
                return current.id;
            }
            current = current.parentNode;
        }

        return null;
    }, [block.id, block.content]);

    const handleClick = useCallback((e) => {
        if (previewMode) {
            return;
        }

        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            let id = findBlockId(e.target);
            const isCmdOrCtrl = e.ctrlKey || e.metaKey;
            const isCmdShift = isCmdOrCtrl && e.shiftKey;
            onRequestToolbarTab('style');
            if (isCmdOrCtrl && !e.shiftKey) {
                onInnerBlockSelect(id);
                return;
            }
            onInnerBlockSelect(id, {
                range: e.shiftKey && !isCmdOrCtrl,
                additive: isCmdShift,
                addOnly: isCmdShift,
            })
            return;
        }

        if (parentSelected || editMode) {
            onRequestToolbarTab('style');
            onBlockSelect(block.id);
            onClick(e);
        }
    }, [previewMode, parentSelected, editMode, onRequestToolbarTab, onInnerBlockSelect, onClick, findBlockId, onBlockSelect, block.id]);

    const handleVideoClick = useCallback((event) => {
        if (previewMode) {
            const hasNativeControls = block.data?.controls ?? true;
            if (hasNativeControls) {
                return;
            }

            const videoElement = event.currentTarget;
            if (videoElement?.paused) {
                videoElement.play?.().catch(() => { });
            } else {
                videoElement.pause?.();
            }
            return;
        }

        handleClick(event);
    }, [previewMode, handleClick, block.data?.controls]);

    const blockActions = useMemo(() => {
        return Array.isArray(block.data?.actions) ? block.data.actions : [];
    }, [block.data?.actions]);

    useEffect(() => {
        if (!previewMode) {
            return;
        }

        const sourceElement = document.getElementById(block.id) || blockRef.current;
        if (!sourceElement || blockActions.length === 0) {
            return;
        }

        const sourceTargetId = block.id;

        const resolveTargetElement = (rawTarget) => {
            let normalizedTarget = typeof rawTarget === 'string' ? rawTarget.trim() : '';
            if (!normalizedTarget) {
                normalizedTarget = sourceTargetId;
            }

            if (normalizedTarget.startsWith('#')) {
                normalizedTarget = normalizedTarget.slice(1);
            }

            const withoutPrefix = normalizedTarget.replace(/^block-/, '');
            return document.getElementById(normalizedTarget)
                || document.getElementById(withoutPrefix)
                || document.getElementById(`block-${withoutPrefix}`)
                || sourceElement;
        };

        const actionsByType = blockActions.reduce((acc, action) => {
            const type = action?.type;
            if (!type) {
                return acc;
            }

            if (!acc[type]) {
                acc[type] = [];
            }

            acc[type].push(action);
            return acc;
        }, {});

        const applyActionEffects = (actions, reset = false) => {
            actions.forEach((action) => {
                const targetElement = resolveTargetElement(action?.target);
                if (!targetElement) {
                    return;
                }

                if (!targetElement.style.transition) {
                    targetElement.style.transition = 'all 0.3s cubic-bezier(0.4,0,0.2,1)';
                }

                if (action?.properties && typeof action.properties === 'object') {
                    Object.entries(action.properties).forEach(([prop, value]) => {
                        if (reset) {
                            targetElement.style.removeProperty(prop);
                            return;
                        }

                        targetElement.style[prop] = value;
                    });
                }

                if (action?.attributes && typeof action.attributes === 'object') {
                    Object.entries(action.attributes).forEach(([attr, value]) => {
                        if (reset) {
                            targetElement.removeAttribute(attr);
                            return;
                        }

                        if (value === null || value === undefined) {
                            targetElement.removeAttribute(attr);
                        } else {
                            targetElement.setAttribute(attr, value);
                        }
                    });
                }
            });
        };

        const clickActions = actionsByType.onClick ?? [];
        const hoverActions = actionsByType.onHover ?? [];

        const handleActionClick = () => {
            if (clickActions.length === 0) {
                return;
            }
            applyActionEffects(clickActions, false);
        };

        const handleActionEnter = () => {
            if (hoverActions.length === 0) {
                return;
            }
            applyActionEffects(hoverActions, false);
        };

        const handleActionLeave = () => {
            if (hoverActions.length === 0) {
                return;
            }
            applyActionEffects(hoverActions, true);
        };

        sourceElement.addEventListener('click', handleActionClick);
        sourceElement.addEventListener('mouseenter', handleActionEnter);
        sourceElement.addEventListener('mouseleave', handleActionLeave);

        return () => {
            sourceElement.removeEventListener('click', handleActionClick);
            sourceElement.removeEventListener('mouseenter', handleActionEnter);
            sourceElement.removeEventListener('mouseleave', handleActionLeave);
        };
    }, [previewMode, blockActions, block.id]);

    const expandBorderRadius = (value) => {
        if (typeof value !== 'string') {
            return [];
        }

        const tokens = value.trim().split(/\s+/).filter(Boolean);
        if (!tokens.length) {
            return [];
        }

        if (tokens.length === 1) {
            return [tokens[0], tokens[0], tokens[0], tokens[0]];
        }

        if (tokens.length === 2) {
            return [tokens[0], tokens[1], tokens[0], tokens[1]];
        }

        if (tokens.length === 3) {
            return [tokens[0], tokens[1], tokens[2], tokens[1]];
        }

        return [tokens[0], tokens[1], tokens[2], tokens[3]];
    };

    const normalizeStyleForView = (styleSource = {}) => {
        const styleObject = { ...(styleSource ?? {}) };
        if (typeof styleObject.borderRadius === 'string' && styleObject.borderRadius.trim() !== '') {
            const [topLeft, topRight = topLeft, bottomRight = topLeft, bottomLeft = topRight] = expandBorderRadius(styleObject.borderRadius);
            if (topLeft && !styleObject.borderTopLeftRadius) {
                styleObject.borderTopLeftRadius = topLeft;
            }
            if (topRight && !styleObject.borderTopRightRadius) {
                styleObject.borderTopRightRadius = topRight;
            }
            if (bottomRight && !styleObject.borderBottomRightRadius) {
                styleObject.borderBottomRightRadius = bottomRight;
            }
            if (bottomLeft && !styleObject.borderBottomLeftRadius) {
                styleObject.borderBottomLeftRadius = bottomLeft;
            }
        }
        delete styleObject.borderRadius;
        return styleObject;
    };

    const composeViewStyle = (baseStyle = {}, override = {}) => ({
        ...normalizeStyleForView(baseStyle),
        ...normalizeStyleForView(override)
    });

    const scaleCssLength = (value, scale, { keepUnitless = false } = {}) => {
        if (!Number.isFinite(scale) || scale === 1 || value === null || value === undefined) {
            return value;
        }

        if (typeof value === 'number') {
            return keepUnitless ? value : value * scale;
        }

        if (typeof value !== 'string') {
            return value;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return value;
        }

        const match = trimmed.match(/^(-?\d*\.?\d+)(px|rem|em|%)?$/i);
        if (!match) {
            return value;
        }

        const numericValue = Number(match[1]);
        const unit = match[2] ?? '';

        if (!Number.isFinite(numericValue)) {
            return value;
        }

        if (!unit && keepUnitless) {
            return value;
        }

        const scaledValue = numericValue * scale;
        const rounded = Number(scaledValue.toFixed(4));
        return `${rounded}${unit}`;
    };

    const applyFontScaleToStyle = (styleObject = {}) => {
        if (!styleObject || !Number.isFinite(fontScale) || fontScale === 1) {
            return styleObject;
        }

        const result = { ...styleObject };

        if (result.fontSize !== undefined) {
            result.fontSize = scaleCssLength(result.fontSize, fontScale);
        }

        if (result.lineHeight !== undefined) {
            result.lineHeight = scaleCssLength(result.lineHeight, fontScale, { keepUnitless: true });
        }

        return result;
    };

    const getViewStyle = () => {
        let result;
        switch (view) {
            case 'desktop': {
                const desktopStyle = normalizeStyleForView(block.style);
                result = desktopStyle;
                break;
            }
            case 'tablet':
                result = composeViewStyle(block.style, block.styleTablet);
                break;
            case 'mobile':
                result = composeViewStyle(
                    composeViewStyle(block.style, block.styleTablet),
                    block.styleMobile
                );
                break;
            default:
                result = normalizeStyleForView(block.style);
                break;
        }

        return applyFontScaleToStyle(result);
    }

    const getButtonStyle = () => {
        const style = { ...(getViewStyle() ?? {}) };
        const widthValue = typeof style.width === 'string'
            ? style.width.trim().toLowerCase()
            : style.width;

        const hasIntrinsicWidth = widthValue !== undefined
            && widthValue !== null
            && widthValue !== ''
            && !['unset', 'initial', 'auto'].includes(widthValue);

        if (!hasIntrinsicWidth) {
            style.width = 'fit-content';
        }

        return style;
    }

    const getGridRows = () => {
        switch (view) {
            case 'desktop':
                return block.data?.grid?.rows
            case 'tablet':
                return block.data?.breakpoints?.[768]?.grid?.rows
            case 'mobile':
                return block.data?.breakpoints?.[540]?.grid?.rows
            default:
                return block.data?.grid?.rows
        }
    }

    const getGridColumns = () => {
        switch (view) {
            case 'desktop':
                return block.data?.grid?.cols
            case 'tablet':
                return block.data?.breakpoints?.[768]?.grid?.cols
            case 'mobile':
                return block.data?.breakpoints?.[540]?.grid?.cols
            default:
                return block.data?.grid?.cols
        }
    }

    const getCarouselViewPerPage = () => {
        switch (view) {
            case 'desktop':
                return block.data?.perPage ?? 1
            case 'tablet':
                return block.data?.breakpoints?.[768]?.perPage ?? block.data?.perPage ?? 1
            case 'mobile':
                return block.data?.breakpoints?.[540]?.perPage ?? block.data?.perPage ?? 1
            default:
                return block.data?.perPage ?? 1
        }
    }

    const getCarouselViewPerMove = () => {
        switch (view) {
            case 'desktop':
                return block.data?.perMove ?? 1
            case 'tablet':
                return block.data?.breakpoints?.[768]?.perMove ?? block.data?.perPage ?? 1
            case 'mobile':
                return block.data?.breakpoints?.[540]?.perMove ?? block.data?.perPage ?? 1
            default:
                return block.data?.perMove ?? 1
        }
    }

    function hashCode(obj) {
        const str = JSON.stringify(obj);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash |= 0; // Converti in intero a 32 bit
        }
        return Math.abs(hash); // Evita numeri negativi
        // return 0
    }

    const carouselSlidesSignature = useMemo(() => {
        return (block.content ?? []).map((child) => child?.id).filter(Boolean).join('|');
    }, [block.content]);

    const carouselRenderKey = useMemo(() => {
        return hashCode({ ...block.data, grid, carouselSlidesSignature });
    }, [block.data, grid, carouselSlidesSignature]);

    const getBlockStyle = () => {
        let result = normalizeFixedBlockStyle(getViewStyle());
        if ([BlockType.Video, BlockType.Icon].includes(block.type)) {
            return { width: result?.width ?? 'fit-content', height: result?.height ?? 'fit-content', maxHeight: result.maxHeight, zIndex: result.zIndex, top: result.top, left: result.left, right: result.right, bottom: result.bottom, position: result.position }
        }
        if (block.type === BlockType.Collapsable) {
            return { width: result?.width ?? '100%' }
        }
        return result
    }

    // Overlay helpers
    const parseBoxValue = (value) => {
        if (!value) return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            if (value.endsWith('px')) return parseFloat(value);
            // fallback: try to convert other units to px (approximate)
            if (value.endsWith('rem')) return parseFloat(value) * getRootFontSize();
            if (value.endsWith('em')) return parseFloat(value) * getRootFontSize();
            if (value.endsWith('%')) return 0; // ignore % for overlay
            return parseFloat(value) || 0;
        }
        return 0;
    };

    // Render margin/padding as bands like browser inspector
    const renderBoxOverlay = (style, type = 'margin') => {
        if (!style) return null;
        const color = type === 'margin' ? MARGIN_OVERLAY_COLOR : PADDING_OVERLAY_COLOR;
        const prefix = type;
        const top = parseBoxValue(style[`${prefix}Top`] ?? style[prefix] ?? 0);
        const bottom = parseBoxValue(style[`${prefix}Bottom`] ?? style[prefix] ?? 0);
        const left = parseBoxValue(style[`${prefix}Left`] ?? style[prefix] ?? 0);
        const right = parseBoxValue(style[`${prefix}Right`] ?? style[prefix] ?? 0);
        if (top + bottom + left + right === 0) return null;

        // Margin: 4 bands outside
        if (type === 'margin') {
            return (
                <>
                    {top > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 100,
                            top: -top,
                            left: -left,
                            right: -right,
                            height: top,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {bottom > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 100,
                            bottom: -bottom,
                            left: -left,
                            right: -right,
                            height: bottom,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {left > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 100,
                            top: 0,
                            left: -left,
                            bottom: 0,
                            width: left,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {right > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 100,
                            top: 0,
                            right: -right,
                            bottom: 0,
                            width: right,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                </>
            );
        }
        // Padding: 4 bands OVER the content area, not inset
        if (type === 'padding') {
            // Top band
            // Bottom band
            // Left band (between top and bottom)
            // Right band (between top and bottom)
            return (
                <>
                    {top > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 101,
                            top: 0,
                            left: 0,
                            right: 0,
                            height: top,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {bottom > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 101,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: bottom,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {left > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 101,
                            top: top,
                            left: 0,
                            bottom: bottom,
                            width: left,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                    {right > 0 && (
                        <div style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            zIndex: 101,
                            top: top,
                            right: 0,
                            bottom: bottom,
                            width: right,
                            background: color,
                            transition: 'background 0.2s',
                        }} />
                    )}
                </>
            );
        }
        return null;
    };

    const getSplideExtensions = () => {
        if (block && block.data) {
            const extensions = {};
            if (block.data.autoScroll) {
                extensions['AutoScroll'] = AutoScroll;
            }
            if (block.data.grid) {
                extensions['Grid'] = Grid;
            }
            return extensions;
        }
    }

    const adjustSplideTransformByFactor = useCallback((transformValue, scaleValue, listElement) => {
        const normalizedScale = (() => {
            const numericScale = Number(scaleValue);
            if (!Number.isFinite(numericScale) || numericScale <= 0) {
                return 1;
            }
            if (numericScale > 1.5) {
                return Math.min(Math.max(numericScale / 100, 0.01), 1);
            }
            return Math.min(Math.max(numericScale, 0.01), 1);
        })();

        if (typeof transformValue !== 'string' || transformValue.trim() === '' || normalizedScale >= 0.999) {
            return transformValue;
        }

        const getTranslateX = (input) => {
            const transform = input ?? '';
            const match3d = transform.match(/translate3d\(\s*(-?\d*\.?\d+)px\s*,/i);
            if (match3d) {
                const value = Number(match3d[1]);
                return Number.isFinite(value) ? value : null;
            }

            const matchX = transform.match(/translateX\(\s*(-?\d*\.?\d+)px\s*\)/i);
            if (matchX) {
                const value = Number(matchX[1]);
                return Number.isFinite(value) ? value : null;
            }

            const match2d = transform.match(/translate\(\s*(-?\d*\.?\d+)px\s*,/i);
            if (match2d) {
                const value = Number(match2d[1]);
                return Number.isFinite(value) ? value : null;
            }

            return null;
        };

        const setTranslateX = (input, xValue) => {
            const xPx = `${Number(xValue.toFixed(4))}px`;
            let output = input;
            output = output.replace(/translate3d\(\s*(-?\d*\.?\d+)px(\s*,[^)]*\))/i, (_, __, tail) => `translate3d(${xPx}${tail})`);
            output = output.replace(/translateX\(\s*(-?\d*\.?\d+)px\s*\)/i, `translateX(${xPx})`);
            output = output.replace(/translate\(\s*(-?\d*\.?\d+)px(\s*,\s*-?\d*\.?\d+px\s*\))/i, (_, __, tail) => `translate(${xPx}${tail})`);
            return output;
        };

        const getCarouselMetrics = (contextElement) => {
            if (!contextElement) {
                return null;
            }

            const nonCloneSlides = Array.from(contextElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
            const slidesCount = nonCloneSlides.length;
            if (!slidesCount) {
                return null;
            }

            let slideStep = 0;
            if (nonCloneSlides.length > 1) {
                const firstOffset = nonCloneSlides[0].offsetLeft;
                const secondOffset = nonCloneSlides[1].offsetLeft;
                const derived = Math.abs(secondOffset - firstOffset);
                if (Number.isFinite(derived) && derived > 0) {
                    slideStep = derived;
                }
            }

            if (slideStep <= 0) {
                const firstSlideWidth = nonCloneSlides[0].getBoundingClientRect().width;
                const computedStyle = typeof window !== 'undefined' && typeof window.getComputedStyle === 'function'
                    ? window.getComputedStyle(nonCloneSlides[0])
                    : null;
                const marginRight = computedStyle ? parseFloat(computedStyle.marginRight) : 0;
                slideStep = (Number.isFinite(firstSlideWidth) ? firstSlideWidth : 0) + (Number.isFinite(marginRight) ? marginRight : 0);
            }

            if (!Number.isFinite(slideStep) || slideStep <= 0) {
                return null;
            }

            const normalizeCount = (value, fallback = 1) => {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) {
                    return fallback;
                }
                const rounded = Math.floor(numeric);
                return rounded > 0 ? rounded : fallback;
            };

            const perPageByView = (() => {
                switch (view) {
                    case 'tablet':
                        return normalizeCount(block.data?.breakpoints?.[768]?.perPage, normalizeCount(block.data?.perPage, 1));
                    case 'mobile':
                        return normalizeCount(block.data?.breakpoints?.[540]?.perPage, normalizeCount(block.data?.perPage, 1));
                    default:
                        return normalizeCount(block.data?.perPage, 1);
                }
            })();

            const perMoveByView = (() => {
                switch (view) {
                    case 'tablet':
                        return normalizeCount(
                            block.data?.breakpoints?.[768]?.perMove
                            ?? block.data?.breakpoints?.[768]?.perPage
                            ?? block.data?.perMove
                            ?? block.data?.perPage,
                            perPageByView
                        );
                    case 'mobile':
                        return normalizeCount(
                            block.data?.breakpoints?.[540]?.perMove
                            ?? block.data?.breakpoints?.[540]?.perPage
                            ?? block.data?.perMove
                            ?? block.data?.perPage,
                            perPageByView
                        );
                    default:
                        return normalizeCount(block.data?.perMove, perPageByView);
                }
            })();

            const perPage = Math.max(Number(perPageByView) || 1, 1);
            const perMove = Math.max(Number(perMoveByView) || 1, 1);
            const pageStep = slideStep * perMove;
            const maxSlideIndex = Math.max(slidesCount - perPage, 0);
            const maxOffset = slideStep * maxSlideIndex;

            return { pageStep, maxOffset };
        };

        const clampOffset = (value, contextElement) => {
            const metrics = getCarouselMetrics(contextElement);
            if (!metrics) {
                const listWidth = contextElement?.scrollWidth ?? 0;
                const viewportWidth = contextElement?.offsetWidth ?? 0;
                const maxRange = Math.max(listWidth, viewportWidth, 1) * 2;
                return Math.min(Math.max(value, -maxRange), maxRange);
            }

            const sign = value < 0 ? -1 : 1;
            const absolute = Math.abs(value);
            const snapped = metrics.pageStep > 0 ? Math.round(absolute / metrics.pageStep) * metrics.pageStep : absolute;
            const bounded = Math.min(snapped, metrics.maxOffset);
            return sign * bounded;
        };

        const currentX = getTranslateX(transformValue);
        if (!Number.isFinite(currentX)) {
            return transformValue;
        }

        const correctionPercent = 1 - normalizedScale;
        const correctedOffset = clampOffset(currentX + (currentX * correctionPercent), listElement);

        if (Math.abs(correctedOffset - currentX) < 0.5) {
            return transformValue;
        }

        return setTranslateX(transformValue, correctedOffset);
    }, [view, block.data]);

    useEffect(() => {
        if (block.type !== BlockType.Carousel) {
            return;
        }

        const target = carouselContainerRef.current
            ?? blockRef.current
            ?? (typeof document !== 'undefined' ? document.getElementById(block.id) : null);
        if (!target) {
            return;
        }

        carouselContainerRef.current = target;
        blockRef.current = target;

        const connectList = (listElement) => {
            if (!listElement) {
                return () => { };
            }

            const syncTransform = () => {
                const currentTransform = listElement.style.transform ?? '';
                if (!currentTransform) {
                    return;
                }

                const getTranslateX = (input) => {
                    const transform = input ?? '';
                    const match3d = transform.match(/translate3d\(\s*(-?\d*\.?\d+)px\s*,/i);
                    if (match3d) {
                        const value = Number(match3d[1]);
                        return Number.isFinite(value) ? value : null;
                    }
                    const matchX = transform.match(/translateX\(\s*(-?\d*\.?\d+)px\s*\)/i);
                    if (matchX) {
                        const value = Number(matchX[1]);
                        return Number.isFinite(value) ? value : null;
                    }
                    const match2d = transform.match(/translate\(\s*(-?\d*\.?\d+)px\s*,/i);
                    if (match2d) {
                        const value = Number(match2d[1]);
                        return Number.isFinite(value) ? value : null;
                    }
                    return null;
                };

                const lastAdjustedX = Number(listElement.dataset.builderAdjustedX);
                const lastRawX = Number(listElement.dataset.builderRawX);
                const currentX = getTranslateX(currentTransform);

                let rawTransform = currentTransform;
                if (Number.isFinite(currentX) && Number.isFinite(lastAdjustedX) && Number.isFinite(lastRawX) && Math.abs(currentX - lastAdjustedX) < 0.5) {
                    const rawXPx = `${Number(lastRawX.toFixed(4))}px`;
                    rawTransform = currentTransform.replace(/translate3d\(\s*(-?\d*\.?\d+)px(\s*,[^)]*\))/i, (_, __, tail) => `translate3d(${rawXPx}${tail})`)
                        .replace(/translateX\(\s*(-?\d*\.?\d+)px\s*\)/i, `translateX(${Number(lastRawX.toFixed(4))}px)`)
                        .replace(/translate\(\s*(-?\d*\.?\d+)px(\s*,\s*-?\d*\.?\d+px\s*\))/i, (_, __, tail) => `translate(${rawXPx}${tail})`);
                }

                const adjustedTransform = adjustSplideTransformByFactor(rawTransform, viewportScale, listElement);

                if (!adjustedTransform || adjustedTransform === currentTransform) {
                    return;
                }

                const adjustedX = getTranslateX(adjustedTransform);
                const rawX = getTranslateX(rawTransform);

                listElement.dataset.builderRawTransform = rawTransform;
                listElement.dataset.builderAdjustedTransform = adjustedTransform;
                if (Number.isFinite(rawX)) {
                    listElement.dataset.builderRawX = String(rawX);
                }
                if (Number.isFinite(adjustedX)) {
                    listElement.dataset.builderAdjustedX = String(adjustedX);
                }
                listElement.dataset.builderApplyingTransform = '1';
                listElement.style.transform = adjustedTransform;
                listElement.dataset.builderApplyingTransform = '0';
            };

            syncTransform();

            const mutationObserver = new MutationObserver((mutationList) => {
                if (listElement.dataset.builderApplyingTransform === '1') {
                    return;
                }

                const hasStyleMutation = mutationList.some((mutation) => mutation.type === 'attributes' && mutation.attributeName === 'style');
                if (!hasStyleMutation) {
                    return;
                }

                syncTransform();
            });

            mutationObserver.observe(listElement, { attributes: true, attributeFilter: ['style'] });

            if (typeof window !== 'undefined') {
                window.addEventListener('resize', syncTransform);
            }

            return () => {
                mutationObserver.disconnect();
                if (typeof window !== 'undefined') {
                    window.removeEventListener('resize', syncTransform);
                }
            };
        };

        let cleanupListObserver = () => { };
        const existingList = target.querySelector('.splide__list');
        if (existingList) {
            cleanupListObserver = connectList(existingList);
        }

        const hostObserver = new MutationObserver(() => {
            const listElement = target.querySelector('.splide__list');
            if (!listElement) {
                return;
            }

            cleanupListObserver();
            cleanupListObserver = connectList(listElement);
        });

        hostObserver.observe(target, { childList: true, subtree: true });

        return () => {
            hostObserver.disconnect();
            cleanupListObserver();
        };
    }, [block.type, block.id, view, viewportWidth, viewportHeight, viewportScale, adjustSplideTransformByFactor]);

    useEffect(() => {
        if (block.type !== BlockType.Carousel || typeof document === 'undefined') {
            return;
        }

        let cleanupTargetListener = () => { };
        let observer;

        const attachListener = () => {
            const target = document.getElementById(block.id);
            if (!target) {
                return false;
            }

            const onCarouselRootClick = (event) => {
                const isCarouselSelected = selectedBlock?.id === block.id;
                const clickedBlockId = findBlockId(event.target);

                if (!isCarouselSelected) {
                    handleClick(event);
                    return;
                }

                if (clickedBlockId && clickedBlockId !== block.id) {
                    const directChildId = findDirectChildBlockId(event.target);
                    const targetId = directChildId ?? clickedBlockId;
                    event.stopPropagation();
                    onRequestToolbarTab('style');
                    onInnerBlockSelect(targetId);
                    return;
                }

                handleClick(event);
            };

            target.addEventListener('click', onCarouselRootClick);
            cleanupTargetListener = () => {
                target.removeEventListener('click', onCarouselRootClick);
            };
            return true;
        };

        const attachedImmediately = attachListener();

        if (!attachedImmediately && document.body) {
            observer = new MutationObserver(() => {
                const attached = attachListener();
                if (attached && observer) {
                    observer.disconnect();
                    observer = null;
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            cleanupTargetListener();
            if (observer) {
                observer.disconnect();
            }
        };
    }, [block.type, block.id, handleClick, findBlockId, findDirectChildBlockId, onInnerBlockSelect, onRequestToolbarTab, selectedBlock?.id]);

    const imageSources = useMemo(() => {
        const desktopSrc = typeof block.data === 'string'
            ? block.data
            : block.data?.url ?? null;

        const mobileSrc = typeof block.data === 'string'
            ? block.data
            : block.data?.urlMobile ?? desktopSrc;

        const shouldUseMobileSource = Number.isFinite(viewportWidth)
            ? viewportWidth <= 768
            : ['tablet', 'mobile'].includes(view);

        const activeSrc = shouldUseMobileSource ? (mobileSrc ?? desktopSrc) : (desktopSrc ?? mobileSrc);

        return {
            desktopSrc,
            mobileSrc,
            activeSrc,
            shouldUseMobileSource
        };
    }, [block.data, viewportWidth, view]);

    // --- RENDER ---
    // Overlay only on selected block
    const showOverlay = Array.isArray(selectedBlockIds)
        ? selectedBlockIds.includes(block.id)
        : selectedBlock?.id === block.id;
    const blockStyle = getBlockStyle();
    const nestedInCarousel = insideCarousel || block.type === BlockType.Carousel;
    const useFloatingDecorators = [BlockType.Image, BlockType.Icon, BlockType.Video, BlockType.Carousel, BlockType.Faq, BlockType.HTML].includes(block.type)
        || (insideCarousel && [BlockType.Section, BlockType.Row, BlockType.Column, BlockType.Markdown, BlockType.Text].includes(block.type));
    const shouldShowFloatingDecorators = useFloatingDecorators && (showOverlay || editMode);
    const [floatingRect, setFloatingRect] = useState(null);

    useEffect(() => {
        if (blockRef.current || typeof document === 'undefined' || !block?.id) {
            return;
        }

        const node = document.getElementById(block.id);
        if (node) {
            blockRef.current = node;
            if (block.type === BlockType.Carousel) {
                carouselContainerRef.current = node;
            }
        }
    }, [block?.id, block?.type, block?.content]);

    useEffect(() => {
        if (!shouldShowFloatingDecorators) {
            setFloatingRect(null);
            return;
        }

        const updateRect = () => {
            const target = blockRef.current;
            if (!target || typeof target.getBoundingClientRect !== 'function') {
                setFloatingRect(null);
                return;
            }
            const rect = target.getBoundingClientRect();
            setFloatingRect((previous) => {
                const next = {
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                };

                if (
                    previous
                    && Math.abs(previous.top - next.top) < 0.5
                    && Math.abs(previous.left - next.left) < 0.5
                    && Math.abs(previous.width - next.width) < 0.5
                    && Math.abs(previous.height - next.height) < 0.5
                ) {
                    return previous;
                }

                return next;
            });
        };

        updateRect();

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && blockRef.current) {
            resizeObserver = new ResizeObserver(() => updateRect());
            resizeObserver.observe(blockRef.current);
        }

        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect, true);

        return () => {
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [shouldShowFloatingDecorators, view, viewportScale]);

    const getBoxValues = (style, type = 'margin') => {
        if (!style) {
            return { top: 0, right: 0, bottom: 0, left: 0 };
        }
        return {
            top: parseBoxValue(style[`${type}Top`] ?? style[type] ?? 0),
            right: parseBoxValue(style[`${type}Right`] ?? style[type] ?? 0),
            bottom: parseBoxValue(style[`${type}Bottom`] ?? style[type] ?? 0),
            left: parseBoxValue(style[`${type}Left`] ?? style[type] ?? 0),
        };
    };

    const getNormalizedViewportScale = () => {
        const numericScale = Number(viewportScale);
        if (!Number.isFinite(numericScale) || numericScale <= 0) {
            return 1;
        }
        if (numericScale > 1.5) {
            return Math.min(Math.max(numericScale / 100, 0.01), 1);
        }
        return Math.min(Math.max(numericScale, 0.01), 1);
    };

    const renderFloatingDecorators = () => {
        if (!shouldShowFloatingDecorators || !floatingRect || typeof document === 'undefined') {
            return null;
        }

        const scale = getNormalizedViewportScale();
        const marginRaw = getBoxValues(blockStyle, 'margin');
        const paddingRaw = getBoxValues(blockStyle, 'padding');
        const margin = {
            top: marginRaw.top * scale,
            right: marginRaw.right * scale,
            bottom: marginRaw.bottom * scale,
            left: marginRaw.left * scale,
        };
        const padding = {
            top: paddingRaw.top * scale,
            right: paddingRaw.right * scale,
            bottom: paddingRaw.bottom * scale,
            left: paddingRaw.left * scale,
        };
        const blockLabel = block.name ? `${block.name} (${block.type})` : block.type;
        const labelOffset = 22 * scale;
        const computedStyle = (typeof window !== 'undefined' && blockRef.current)
            ? window.getComputedStyle(blockRef.current)
            : null;
        const secondaryRgb = computedStyle?.getPropertyValue('--secondary-rgb')?.trim() || '109, 74, 255';
        const sfGreenRgb = computedStyle?.getPropertyValue('--sf-green-rgb')?.trim() || '0, 200, 80';
        const sfOrangeRgb = computedStyle?.getPropertyValue('--sf-orange-rgb')?.trim() || '255, 140, 0';
        const labelBackgroundColor = block.type === BlockType.Row
            ? `rgba(${sfGreenRgb}, 1)`
            : block.type === BlockType.Column
                ? `rgba(${sfOrangeRgb}, 1)`
                : `rgba(${secondaryRgb}, 1)`;

        return createPortal((
            <>
                {showOverlay && (
                    <>
                        {margin.top > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1200,
                                top: floatingRect.top - margin.top,
                                left: floatingRect.left - margin.left,
                                width: floatingRect.width + margin.left + margin.right,
                                height: margin.top,
                                background: MARGIN_OVERLAY_COLOR,
                            }} />
                        )}
                        {margin.bottom > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1200,
                                top: floatingRect.top + floatingRect.height,
                                left: floatingRect.left - margin.left,
                                width: floatingRect.width + margin.left + margin.right,
                                height: margin.bottom,
                                background: MARGIN_OVERLAY_COLOR,
                            }} />
                        )}
                        {margin.left > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1200,
                                top: floatingRect.top,
                                left: floatingRect.left - margin.left,
                                width: margin.left,
                                height: floatingRect.height,
                                background: MARGIN_OVERLAY_COLOR,
                            }} />
                        )}
                        {margin.right > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1200,
                                top: floatingRect.top,
                                left: floatingRect.left + floatingRect.width,
                                width: margin.right,
                                height: floatingRect.height,
                                background: MARGIN_OVERLAY_COLOR,
                            }} />
                        )}

                        {padding.top > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1201,
                                top: floatingRect.top,
                                left: floatingRect.left,
                                width: floatingRect.width,
                                height: padding.top,
                                background: PADDING_OVERLAY_COLOR,
                            }} />
                        )}
                        {padding.bottom > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1201,
                                top: floatingRect.top + Math.max(floatingRect.height - padding.bottom, 0),
                                left: floatingRect.left,
                                width: floatingRect.width,
                                height: Math.min(padding.bottom, floatingRect.height),
                                background: PADDING_OVERLAY_COLOR,
                            }} />
                        )}
                        {padding.left > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1201,
                                top: floatingRect.top + padding.top,
                                left: floatingRect.left,
                                width: padding.left,
                                height: Math.max(floatingRect.height - padding.top - padding.bottom, 0),
                                background: PADDING_OVERLAY_COLOR,
                            }} />
                        )}
                        {padding.right > 0 && (
                            <div style={{
                                pointerEvents: 'none',
                                position: 'fixed',
                                zIndex: 1201,
                                top: floatingRect.top + padding.top,
                                left: floatingRect.left + Math.max(floatingRect.width - padding.right, 0),
                                width: Math.min(padding.right, floatingRect.width),
                                height: Math.max(floatingRect.height - padding.top - padding.bottom, 0),
                                background: PADDING_OVERLAY_COLOR,
                            }} />
                        )}
                    </>
                )}

                {editMode && (
                    <div
                        className={`${styles.blockType} ${blockSelectedStyles[block.type] ?? ''}`}
                        style={{
                            position: 'fixed',
                            top: floatingRect.top - labelOffset,
                            left: floatingRect.left,
                            zIndex: 1300,
                            color: 'white',
                            backgroundColor: labelBackgroundColor,
                        }}
                    >
                        {blockLabel}
                    </div>
                )}
            </>
        ), document.body);
    };

    return (
        <>
            {[BlockType.Section, BlockType.Column, BlockType.Row].includes(block.type) && (
                <div
                    ref={blockRef}
                    id={block.id}
                    className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                    style={blockStyle}
                    onClick={handleClick}
                >
                    {/* Overlay for margin/padding */}
                    {!useFloatingDecorators && showOverlay && (
                        <>
                            {renderBoxOverlay(blockStyle, 'margin')}
                            {renderBoxOverlay(blockStyle, 'padding')}
                        </>
                    )}

                    {!useFloatingDecorators && editMode &&
                        <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                            {block.name ? `${block.name} (${block.type})` : block.type}
                        </div>
                    }
                    <Each of={block.content} render={(childBlock) => {
                        return <PageBlock
                            element={element}
                            view={view}
                            grid={grid}
                            fontScale={fontScale}
                            viewportScale={viewportScale}
                            previewMode={previewMode}
                            key={childBlock.id}
                            block={childBlock}
                            selectedBlock={selectedBlock}
                            selectedBlockIds={selectedBlockIds}
                            viewportSize={viewportSize}
                            parentSelected={editMode}
                            insideCarousel={nestedInCarousel}
                            onBlockAdd={(id, type) => handleBlockAdd(id, type)}
                            onBlockSelect={(cb, options) => {
                                setEditMode(false)
                                onBlockSelect(cb, options)
                            }}
                            onDataUpdate={onDataUpdate}
                            onContentOrder={(id, newContent) => { onContentOrder(id, newContent) }}
                            openBlockDialog={openBlockDialog}
                            onInnerBlockSelect={onInnerBlockSelect}
                            onRequestToolbarTab={onRequestToolbarTab}
                        />
                    }} />
                </div>
            )}
            {[BlockType.Markdown, BlockType.Text].includes(block.type) && (
                <div
                    className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                    id={block.id}
                    ref={blockRef}
                    style={getViewStyle()}
                    onClick={handleClick}>
                    {showOverlay && (
                        <>
                            {renderBoxOverlay(blockStyle, 'margin')}
                            {renderBoxOverlay(blockStyle, 'padding')}
                        </>
                    )}
                    {editMode &&
                        <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                            {block.name ? `${block.name} (${block.type})` : block.type}
                        </div>
                    }
                    <Markdown
                        rehypePlugins={[rehypeRaw]}>{block.data ?? ''}</Markdown>
                </div>
            )}
            {block.type === BlockType.Image && (
                <>
                    {imageSources.activeSrc ? (
                        <>
                            {/* Overlay for margin/padding */}
                            {!useFloatingDecorators && showOverlay && (
                                <>
                                    {renderBoxOverlay(blockStyle, 'margin')}
                                    {renderBoxOverlay(blockStyle, 'padding')}
                                </>
                            )}
                            {!useFloatingDecorators && editMode &&
                                <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                                    {block.name ? `${block.name} (${block.type})` : block.type}
                                </div>
                            }
                            <picture style={{ display: 'contents' }}>
                                {imageSources.mobileSrc && (
                                    <source
                                        media={imageSources.shouldUseMobileSource ? 'all' : 'not all'}
                                        srcSet={imageSources.mobileSrc}
                                    />
                                )}
                                {imageSources.desktopSrc && (
                                    <source
                                        media={imageSources.shouldUseMobileSource ? 'not all' : 'all'}
                                        srcSet={imageSources.desktopSrc}
                                    />
                                )}
                                <img
                                    ref={blockRef}
                                    id={block.id}
                                    src={imageSources.activeSrc}
                                    alt={typeof block.data === 'object' ? (block.data?.alt ?? '') : ''}
                                    draggable="false"
                                    className={`${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                                    style={blockStyle}
                                    onClick={handleClick}
                                />
                            </picture>
                        </>
                    ) : (
                        <div
                            ref={blockRef}
                            id={block.id}
                            className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                            style={blockStyle}
                            onClick={handleClick}
                        >
                            {/* Overlay for margin/padding */}
                            {!useFloatingDecorators && showOverlay && (
                                <>
                                    {renderBoxOverlay(blockStyle, 'margin')}
                                    {renderBoxOverlay(blockStyle, 'padding')}
                                </>
                            )}
                            <ImageIcon ref={blockRef} id={block.id} className={styles.imageIcon} />
                        </div>
                    )}
                </>
            )}
            {block.type === BlockType.Icon && (
                <>
                    {block.data?.icon?.data &&
                        <SvgRenderer id={block.id} ref={blockRef} svgString={block.data?.icon?.data} style={getViewStyle()} onClick={handleClick} />
                    }
                    {!block.data?.icon?.data &&
                        <div
                            ref={blockRef}
                            id={block.id}
                            className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                            style={getViewStyle()}
                            onClick={handleClick}
                        >
                            Select an icon
                        </div>
                    }
                </>
            )}
            {block.type === BlockType.Video && (
                <>
                    {block.data && block.data !== '' &&
                        <video src={block.data.url} style={getViewStyle()}
                            ref={blockRef}
                            id={block.id}
                            className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                            controls={previewMode ? (block.data.controls ?? true) : false}
                            autoPlay={block.data.autoplay ?? false}
                            loop={block.data.loop ?? false}
                            muted={block.data.muted ?? false}
                            poster={block.data.thumbnail}
                            playsInline={block.data.playsInline ?? false}
                            onClick={handleVideoClick}
                        />
                    }
                    {(!block.data || block.data === '') &&
                        <div
                            ref={blockRef}
                            id={block.id}
                            className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                            style={blockStyle}
                            onClick={handleClick}
                        >
                            <VideoIcon className={styles.imageIcon} />
                        </div>
                    }
                </>
            )}
            {block.type === BlockType.Button && (
                <Button
                    id={block.id}
                    ref={blockRef}
                    style={getButtonStyle()}
                    accentColor={block.data?.accentColor}
                    textColor={block.data?.textColor}
                    inverse={block.data?.inverse ?? false}
                    appearance={block.data?.appearance ?? 'default'}
                    onClick={handleClick}
                >
                    <>
                        {showOverlay && (
                            <>
                                {renderBoxOverlay(blockStyle, 'margin')}
                                {renderBoxOverlay(blockStyle, 'padding')}
                            </>
                        )}
                        {editMode &&
                            <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                                {block.name ? `${block.name} (${block.type})` : block.type}
                            </div>
                        }
                        {block.data?.icon?.data && (['left', 'top'].includes(block.data?.icon?.position) || !block.data?.icon?.position) &&
                            <SvgRenderer svgString={block.data?.icon?.data} style={block.data?.icon?.style} />
                        }
                        {block.data?.on_click === 'purchase' &&
                            <>
                                {block.data?.text}
                                {(!block.data?.text || block.data?.text === '') && !block.data?.icon &&
                                    <>ACQUISTA</>
                                }
                            </>
                        }
                        {block.data?.on_click === 'apply' &&
                            <>
                                {block.data?.text}
                                {(!block.data?.text || block.data?.text === '') && !block.data?.icon &&
                                    <>CANDIDATI</>
                                }
                            </>
                        }
                        {block.data?.on_click === 'brochure' && (block.data?.text || (!block.data?.icon && 'SCARICA BROCHURE'))}
                        {!['purchase', 'apply', 'brochure'].includes(block.data?.on_click) &&
                            <>
                                {block.data?.text ?? "Bottone"}
                            </>
                        }
                        {block.data?.icon?.data && ['right', 'bottom'].includes(block.data?.icon?.position) &&
                            <SvgRenderer svgString={block.data?.icon?.data} style={block.data?.icon?.style} />
                        }
                    </>

                </Button>
            )}
            {block.type === BlockType.HTML && (
                <div id={block.id}
                    ref={blockRef}
                    onPointerDownCapture={handleHtmlInteraction}
                    onClickCapture={handleHtmlInteraction}
                    onAuxClickCapture={handleHtmlInteraction}
                    onSubmitCapture={handleHtmlInteraction}
                    onClick={handleClick}
                    style={getViewStyle()}
                    className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                    dangerouslySetInnerHTML={{ __html: htmlTypographyPreview(block.data?.html, view) }} />
            )}
            {block.type === BlockType.Carousel && (
                <Splide
                    id={block.id}
                    className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                    style={getViewStyle()}
                    key={carouselRenderKey}
                    extensions={getSplideExtensions()}
                    options={{
                        type: block.data?.type ?? 'slide',
                        drag: block.data?.drag,
                        autoplay: block.data?.autoplay,
                        grid: block.data?.grid ? { rows: getGridRows(), cols: getGridColumns() } : null,
                        speed: block.data?.speed ?? 300,
                        perPage: getCarouselViewPerPage(),
                        perMove: getCarouselViewPerMove(),
                        rewind: block.data?.rewind,
                        width: block.data?.width,
                        height: block.data?.height,
                        autoWidth: block.data?.autoWidth,
                        autoHeight: block.data?.autoHeight,
                        gap: block.data?.gap,
                        arrows: block.data?.arrows ?? true,
                        pagination: block.data?.pagination ?? true,
                        direction: block.data?.direction,
                        autoScroll: {
                            speed: block.data?.autoScroll?.speed ?? 0,
                            rewind: block.data?.autoScroll?.rewind ?? false,
                            pauseOnHover: block.data?.autoScroll?.pauseOnHover ?? false,
                            pauseOnFocus: block.data?.autoScroll?.pauseOnFocus ?? false,
                        },
                        breakpoints: block.data?.breakpoints,
                    }}
                >
                    <Each of={block.content ?? []} render={(childBlock) => {
                        return <SplideSlide key={childBlock.id}>
                            <PageBlock
                                view={view}
                                grid={grid}
                                fontScale={fontScale}
                                viewportScale={viewportScale}
                                previewMode={previewMode}
                                key={childBlock.id}
                                block={childBlock}
                                selectedBlock={selectedBlock}
                                selectedBlockIds={selectedBlockIds}
                                viewportSize={viewportSize}
                                parentSelected={editMode}
                                insideCarousel={nestedInCarousel}
                                onBlockAdd={(id, type) => handleBlockAdd(id, type)}
                                onBlockSelect={(cb, options) => {
                                    setEditMode(false)
                                    onBlockSelect(cb, options)
                                }}
                                onDataUpdate={onDataUpdate}
                                onContentOrder={(id, newContent) => { onContentOrder(id, newContent) }}
                                openBlockDialog={openBlockDialog}
                                onInnerBlockSelect={onInnerBlockSelect}
                                onRequestToolbarTab={onRequestToolbarTab}
                            />
                        </SplideSlide>
                    }} />
                </Splide>
            )}
            {block.type === BlockType.Collapsable && (
                <CollapsableBlock
                    id={block.id}
                    ref={blockRef}
                    onClick={handleClick}
                    headerStyle={block.data?.headerStyle}
                    title={block.data?.title}
                    style={getViewStyle()}
                    disabled={!previewMode && !editMode}>
                    {showOverlay && (
                        <>
                            {renderBoxOverlay(blockStyle, 'margin')}
                            {renderBoxOverlay(blockStyle, 'padding')}
                        </>
                    )}
                    {editMode &&
                        <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                            {block.name ? `${block.name} (${block.type})` : block.type}
                        </div>
                    }
                    <Each of={block.content ?? []} render={(childBlock) => {
                        return <PageBlock
                            view={view}
                            grid={grid}
                            fontScale={fontScale}
                            viewportScale={viewportScale}
                            previewMode={previewMode}
                            key={childBlock.id}
                            block={childBlock}
                            selectedBlock={selectedBlock}
                            selectedBlockIds={selectedBlockIds}
                            viewportSize={viewportSize}
                            parentSelected={editMode}
                            insideCarousel={nestedInCarousel}
                            onBlockAdd={(id, type) => handleBlockAdd(id, type)}
                            onBlockSelect={(cb, options) => {
                                setEditMode(false)
                                onBlockSelect(cb, options)
                            }}
                            onDataUpdate={onDataUpdate}
                            onContentOrder={(id, newContent) => { onContentOrder(id, newContent) }}
                            openBlockDialog={openBlockDialog}
                            onInnerBlockSelect={onInnerBlockSelect}
                            onRequestToolbarTab={onRequestToolbarTab}
                        />

                    }} />
                </CollapsableBlock>
            )}
            {block.type === BlockType.Faq && (
                <FAQCollapsible id={block.id} ref={blockRef} onClick={handleClick} style={getViewStyle()} textStyle={block.data?.textStyle} accentColor={block.data?.accentColor} title={block.data?.title} description={block.data?.description} disabled={!previewMode && !editMode} />
            )}
            {block.type === BlockType.Editions && (
                <EditionsCarousel id={block.id} ref={blockRef} course_id={block.data?.course_id ?? element?.id} accentColor={block.data?.accentColor} style={getViewStyle()} onClick={handleClick} />
            )}


            {![BlockType.HTML, BlockType.Image, BlockType.Faq, BlockType.Icon, BlockType.Editions, BlockType.Video, BlockType.Button, BlockType.Carousel, BlockType.Markdown, BlockType.Text, BlockType.Section, BlockType.Column, BlockType.Row, BlockType.Collapsable].includes(block.type) &&
                <div
                    ref={blockRef}
                    id={block.id}
                    className={`${blockStyles[block.type]} ${((!content || content?.length === 0) && (!block.data || block.data === '' || Object.keys(block.data).length === 0)) ? styles.empty : ''} ${grid ? styles.grid : ''} ${editMode ? styles.selected : ''} ${isParentOfSelected ? styles.dashed : ''}`}
                    style={blockStyle}
                    onClick={handleClick}
                >
                    {/* Overlay for margin/padding */}
                    {showOverlay && (
                        <>
                            {renderBoxOverlay(blockStyle, 'margin')}
                            {renderBoxOverlay(blockStyle, 'padding')}
                        </>
                    )}

                    {editMode &&
                        <div className={`${styles.blockType} ${blockSelectedStyles[block.type]}`}>
                            {block.name ? `${block.name} (${block.type})` : block.type}
                        </div>
                    }






                    {block.type === BlockType.Form &&
                        <ContactFormBlock style={getViewStyle()} data={block?.data ?? {}} disabled={!editMode} />
                    }


                </div >
            }

            {renderFloatingDecorators()}
        </>
    );
}

export default PageBlock;
